import React, { useContext, useEffect } from 'react';
import { FilterContext } from '../../components/FilterBar/FilterProvider';
import { useLocation } from 'react-router-dom';
import { helperFunctions } from '../../utils/helperFunctions';
import { OverviewService } from '../../service/overviewService';
import GlobalOverview from 'containers/GlobalOverview';
import { registry, TAB_OVERVIEW } from './tabRegistry';
import store from '../../store/store';
import LayoutV2 from '../LayoutV2';
import { useCmsLayout } from '../../hooks/useCmsLayout';

const Dashboard = React.memo(() => {
  const {
    selectedProduct,
    selectedProgram,
    // selectedSector,
    productValues,
    setSelectedProduct,
    selectedModel,
    betaFeatureToggle,
  } = useContext(FilterContext);

  const location = useLocation();
  const { state } = location;
  const { selectedProductId } = state || {};
  const isMFE = store.getState().app.isMFE;
  let defaultValue = 1;
  if (productValues && productValues.length > 0) {
    defaultValue = productValues[0].productId;
  }
  const productId = selectedProductId || defaultValue;
  const { isDevOrQaOrPreProdEnv } = helperFunctions();

  // Fetch and cache CMS layout data (for future use)
  useCmsLayout();

  useEffect(() => {
    if (Array.isArray(productValues) && productValues.length > 0) {
      const filteredProduct = productValues.find(
        (prod) => prod.productId === productId
      );
      setSelectedProduct(filteredProduct || null);
    }
  }, [productValues, setSelectedProduct, productId]);

  // Build a config object to be passed to each isAvailable function.
  const config = {
    selectedProduct,
    selectedModel,
    betaFeatureToggle,
    isMFE,
    isDevOrQaOrPreProdEnv,
  };

  // Get the tab configurations for the selected product.
  // If no specific registry exists, fall back to the default configuration.
  const productTabs = registry[selectedProduct?.productId] || registry.default;

  // Filter out any tab whose isAvailable function (if defined) returns false.
  const availableTabs = productTabs
    .filter(({ isAvailable }) =>
      typeof isAvailable === 'function' ? isAvailable(config) : true
    )
    .map(({ tab }) => tab);

  // Check if there is a custom Overview already (i.e. one not using GlobalOverview).
  const hasCustomOverview = availableTabs.some(
    (tab) =>
      tab.label.toLowerCase().includes('overview') &&
      tab.component !== GlobalOverview
  );

  // Determine if GlobalOverview has content.
  const overviewFilters = {
    product: selectedProduct?.productId,
    program: selectedProgram?.programId,
  };
  const defaultScoreCards = OverviewService.resolveScoreCards(overviewFilters);
  const defaultScoreCardsExist = defaultScoreCards.length > 0;
  const CustomOverviewComponent =
    OverviewService.resolveCustomOverview(overviewFilters);
  const showGlobalOverview = defaultScoreCardsExist || CustomOverviewComponent;

  // If no custom Overview exists and GlobalOverview should be shown, prepend it.
  // But if the first tab is Success Report, insert Overview after it.
  let finalTabs = availableTabs;
  if (!hasCustomOverview && showGlobalOverview) {
    const overviewTab = {
      id: TAB_OVERVIEW,
      label: 'Overview',
      component: GlobalOverview,
    };

    // Check if the first tab is Success Report
    const firstTabIsSuccessReport =
      availableTabs.length > 0 && availableTabs[0].id === 'successReport';

    if (firstTabIsSuccessReport) {
      // Insert Overview as the second tab, keeping Success Report first
      finalTabs = [availableTabs[0], overviewTab, ...availableTabs.slice(1)];
    } else {
      // Prepend Overview as usual
      finalTabs = [overviewTab, ...availableTabs];
    }
  }

  return (
    <div
      data-testid="dashboard"
      id="dashboard"
      style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      {selectedProduct && (
        <LayoutV2
          tabArray={finalTabs}
          product={selectedProduct}
          model={selectedModel}
        />
      )}
    </div>
  );
});

Dashboard.displayName = 'Dashboard';
export default Dashboard;
